import { parseLsBlocks, type LsBlock } from "../ls/parse-blocks.js";

const label = (value: string) => value.toLowerCase().replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
function proseLine(block: LsBlock): string | null {
  switch (block.kind) {
    case "dialogue": return `${label(block.speaker)}: ${block.text}`;
    case "narration": return block.speaker === "YOU" ? `(You think) ${block.text}` : block.text;
    case "choice": return "[Choice]";
    case "option": return `[Option: ${block.text ?? label(block.id ?? "")}]`;
    case "if": {
      const condition = block.condition?.replace(/check.success/g, "the check succeeds").replace(/check.fail/g, "the check fails")
        .replace(/affection\.([\w-]+)/g, (_, name: string) => `${label(name)}'s affection`)
        .replace(/>=/g, "is at least").replace(/<=/g, "is at most").replace(/&&/g, "and").replace(/\|\|/g, "or");
      return `[${condition ? `If ${condition}` : "Otherwise"}${block.next ? ` — continue to ${block.next}` : ""}]`;
    }
    case "affection": return `[Effect: ${label(block.character ?? "Character")} affection ${block.delta}]`;
    case "signal": return `[Effect: ${[label(block.name ?? ""), block.op, block.value].filter(Boolean).join(" ")}]`;
    default: return null;
  }
}
interface ProseEntry { block: LsBlock; text: string; }
function entries(source: string): ProseEntry[] {
  return parseLsBlocks(source).flatMap((block) => {
    const text = proseLine(block);
    return text === null ? [] : [{ block, text }];
  });
}

/** Plain manuscript with readable story beats; original script retains all rules. */
export function scriptToProse(source: string): string {
  return entries(source).map((entry) => entry.text).join("\n\n").trim();
}

/** Translate the caret into a real playable passage, including repeated dialogue. */
export function passageAtCursor(source: string, prose: string, cursor: number): { text: string; speaker?: string; occurrence: number } | null {
  const paragraphs = prose.split(/\n\s*\n/);
  const separators = [...prose.matchAll(/\n\s*\n/g)];
  const index = separators.filter((match) => cursor >= match.index! + match[0].length).length;
  const rows = entries(source);
  // After AI conversion, match the author-visible paragraph to compiled script text.
  const paragraph = paragraphs[index]?.trim() ?? "";
  let selected = rows[index];
  if (!selected || selected.text !== paragraph) {
    selected = rows.find((row) => row.text === paragraph || ((row.block.kind === "dialogue" || row.block.kind === "narration") && paragraph.includes(row.block.text)));
  }
  if (!selected) return null;
  let rowIndex = rows.indexOf(selected);
  // A choice/rule heading previews the next playable sentence in that branch.
  while (rowIndex < rows.length && rows[rowIndex]!.block.kind !== "dialogue" && rows[rowIndex]!.block.kind !== "narration") rowIndex++;
  const block = rows[rowIndex]?.block;
  if (!block || (block.kind !== "dialogue" && block.kind !== "narration")) return null;
  const speaker = block.kind === "dialogue" ? block.speaker : undefined;
  const occurrence = rows.slice(0, rowIndex).filter(({ block: candidate }) =>
    (candidate.kind === "dialogue" || candidate.kind === "narration") && candidate.text === block.text &&
    (!speaker || candidate.kind === "dialogue" && candidate.speaker === speaker)).length;
  return { text: block.text, ...(speaker ? { speaker } : {}), occurrence };
}

function dialogueText(speaker: string, paragraph: string): string | null {
    for (const name of [label(speaker), speaker]) {
      for (const colon of [":", "："]) {
        if (paragraph.startsWith(name + colon)) return paragraph.slice(name.length + 1).trimStart();
      }
    }
    return null;
}

function authoredText(block: LsBlock, paragraph: string): string | null {
  if (block.kind === "dialogue") return dialogueText(block.speaker, paragraph);
  if (block.kind !== "narration") return null;
  if (block.speaker !== "YOU") return paragraph;
  for (const prefix of ["(You think) ", "（YOU）", "(YOU)"]) {
    if (paragraph.startsWith(prefix)) return paragraph.slice(prefix.length).trimStart();
  }
  return paragraph.startsWith("（") && paragraph.endsWith("）") ? paragraph.slice(1, -1) : null;
}

/** Immediate prose edits and one contiguous paragraph insertion retain the source structure. */
export function patchProseText(source: string, before: string, after: string): string | null {
  const rows = entries(source);
  const oldParagraphs = before.split(/\n\s*\n/);
  const paragraphs = after.split(/\n\s*\n/);
  if (oldParagraphs.length !== rows.length) return null;
  // Older local drafts use full-width punctuation and metadata labels. Validate
  // every original sentence against its source rather than requiring new UI labels.
  for (let i = 0; i < rows.length; i++) {
    const { block, text } = rows[i]!;
    if (block.kind === "dialogue" || block.kind === "narration") {
      if (authoredText(block, oldParagraphs[i]!) !== block.text) return null;
    } else if (oldParagraphs[i] !== text && !/^〔.*〕$/.test(oldParagraphs[i]!)) return null;
  }
  const lines = source.split("\n");
  const added = paragraphs.length - oldParagraphs.length;
  if (added > 0) {
    let at = 0;
    while (at < oldParagraphs.length && oldParagraphs[at] === paragraphs[at]) at++;
    if (oldParagraphs.slice(at).some((text, index) => text !== paragraphs[at + added + index])) return null;
    const anchor = rows[at - 1]?.block;
    if (!anchor || (anchor.kind !== "dialogue" && anchor.kind !== "narration")) return null;
    const indent = lines[anchor.line - 1]!.match(/^\s*/)?.[0] ?? "";
    const speakers = new Set(rows.flatMap(({ block }) => block.kind === "dialogue" ? [block.speaker] : []));
    const inserted: string[] = [];
    for (const paragraph of paragraphs.slice(at, at + added)) {
      if (!paragraph.trim() || /[\n{}]/.test(paragraph) || /^\s*[@[〔]/.test(paragraph)) return null;
      let speaker = "NARRATOR";
      let text = paragraph;
      for (const name of speakers) {
        const dialogue = dialogueText(name, paragraph);
        if (dialogue !== null) { speaker = name; text = dialogue; break; }
      }
      if (speaker === "NARRATOR" && /^[\p{L}\w-]+[:：]/u.test(paragraph)) return null;
      inserted.push(`${indent}${speaker}: ${text}`);
    }
    lines.splice(anchor.line, 0, ...inserted);
    return lines.join("\n");
  }
  if (added !== 0) return null;
  for (let i = 0; i < rows.length; i++) {
    if (oldParagraphs[i] === paragraphs[i]) continue;
    const block = rows[i]!.block;
    const text = authoredText(block, paragraphs[i]!);
    if (text === null || /[\n{}]/.test(text) || (block.kind !== "dialogue" && block.kind !== "narration")) return null;
    const indent = lines[block.line - 1]!.match(/^\s*/)?.[0] ?? "";
    const pose = block.kind === "dialogue" && block.pose ? ` [${block.pose}]` : "";
    lines[block.line - 1] = `${indent}${block.speaker}${pose}: ${text}`;
  }
  return lines.join("\n");
}
